import React, { useState } from "react";
import { Printer, UserPlus, User, Plus, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import api, { apiError } from "@/lib/api";
import { useLang } from "@/i18n";
import { PRINT_LANGS } from "@/lib/printI18n";
import { langName } from "@/lib/constants";

const today = () => new Date().toISOString().slice(0, 10);
const empty = { email: "", first_name: "", last_name: "" };

export default function PrintPlusDialog({ withLabel = false }) {
  const { t, lang: uiLang } = useLang();
  const [open, setOpen] = useState(false);
  const [lang, setLang] = useState(uiLang);
  const [from, setFrom] = useState(today());
  const [to, setTo] = useState("");
  const [f, setF] = useState(empty);
  const [found, setFound] = useState(null);
  const [sendCard, setSendCard] = useState(true);
  const [busy, setBusy] = useState(false);

  const reset = () => { setF(empty); setFound(null); setFrom(today()); setTo(""); setLang(uiLang); setSendCard(true); };
  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));

  const lookup = async () => {
    const email = f.email.trim();
    if (!email) return;
    try {
      const { data } = await api.get(`/guests/by-email`, { params: { email } });
      if (data.found) {
        const g = data.guest;
        setFound(g);
        setF((s) => ({ ...s, first_name: s.first_name || g.first_name || "", last_name: s.last_name || g.last_name || "" }));
        if (g.language) setLang(g.language);
        toast.success(t("existing_guest_found"));
      } else { setFound(null); }
    } catch { /* genegeerd */ }
  };

  const longStay = to && from && (new Date(to) - new Date(from)) / 86400000 > 183;

  const submit = async (confirmLong = false) => {
    if (!to || to < from) { toast.error(t("pick_range_hint")); return; }
    if (!f.first_name || !f.last_name) { toast.error(t("name_required")); return; }
    if (longStay && !confirmLong) {
      if (!window.confirm(t("confirm_long_stay"))) return;
      confirmLong = true;
    }
    setBusy(true);
    try {
      const email = f.email.trim();
      const { data } = await api.post(`/arrivals/quick`, {
        email, first_name: f.first_name.trim(), last_name: f.last_name.trim(),
        arrival: from, departure: to, language: lang, confirm_long: confirmLong,
        send_card: sendCard && !!email,
      });
      toast.success(data.existing ? `${t("reservation_created")} · ${t("existing_guest")}` : t("reservation_created"));
      if (data.card_sent && data.email) toast.success(t("card_sent_to").replace("{email}", data.email));
      setOpen(false);
      reset();
      setTimeout(() => window.open(`/print?from=${from}&to=${to}&lang=${lang}&res=${data.reservation_id}&map=1`, "_blank"), 150);
    } catch (e) {
      const d = e.response?.data?.detail;
      if (d?.code === "confirm_long") { if (window.confirm(t("confirm_long_stay"))) submit(true); }
      else toast.error(apiError(d) || e.message);
    } finally { setBusy(false); }
  };

  return (
    <>
      <Button variant="outline" size="sm" className="h-8 px-2"
        onClick={() => setOpen(true)} data-testid="printplus-button" title={t("print_plus_hint")}>
        <span className="flex items-center gap-0.5">
          <Printer className="h-4 w-4" />
          <Plus className="h-2.5 w-2.5" strokeWidth={3} />
          <User className="h-4 w-4" />
        </span>
        {withLabel && <span className="ml-1.5">{t("print_plus")}</span>}
      </Button>
      <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) reset(); }}>
        <DialogContent className="sm:max-w-lg" data-testid="printplus-dialog">
          <DialogHeader><DialogTitle className="font-display flex items-center gap-2"><UserPlus className="h-4 w-4" />{t("print_plus_title")}</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">{t("print_plus_desc")}</p>

          <div className="space-y-1.5">
            <Label htmlFor="pp-email">{t("email")}</Label>
            <div className="flex gap-2">
              <Input id="pp-email" type="email" value={f.email} onChange={set("email")} onBlur={lookup}
                placeholder="naam@voorbeeld.be" data-testid="printplus-email" />
              <Button variant="outline" size="sm" onClick={lookup} data-testid="printplus-lookup">{t("check")}</Button>
            </div>
            {found && (
              <p className="text-xs text-emerald-600 flex items-center gap-1" data-testid="printplus-found">
                <CheckCircle2 className="h-3.5 w-3.5" />{t("existing_guest_found")}
              </p>
            )}
            {f.email.trim() && (
              <label className="flex items-center gap-2 pt-1 cursor-pointer" htmlFor="pp-send-card">
                <Checkbox id="pp-send-card" checked={sendCard} onCheckedChange={(v) => setSendCard(!!v)} data-testid="printplus-send-card" />
                <span className="text-sm text-muted-foreground">{t("print_plus_send_card")}</span>
              </label>
            )}
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label htmlFor="pp-first">{t("first_name")} *</Label>
              <Input id="pp-first" value={f.first_name} onChange={set("first_name")} data-testid="printplus-first" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="pp-last">{t("last_name")} *</Label>
              <Input id="pp-last" value={f.last_name} onChange={set("last_name")} data-testid="printplus-last" />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label htmlFor="pp-from">{t("from")}</Label>
              <Input id="pp-from" type="date" value={from} onChange={(e) => setFrom(e.target.value)} data-testid="printplus-from" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="pp-to">{t("to")}</Label>
              <Input id="pp-to" type="date" min={from} value={to} onChange={(e) => setTo(e.target.value)} data-testid="printplus-to" />
            </div>
          </div>
          {longStay && <p className="text-xs text-amber-600" data-testid="printplus-long-warn">{t("confirm_long_stay")}</p>}

          <div className="space-y-1.5">
            <Label>{t("language")}</Label>
            <Select value={lang} onValueChange={setLang}>
              <SelectTrigger data-testid="printplus-lang"><SelectValue /></SelectTrigger>
              <SelectContent>{PRINT_LANGS.map((l) => <SelectItem key={l.code} value={l.code}>{langName(l.code, uiLang)}</SelectItem>)}</SelectContent>
            </Select>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>{t("cancel")}</Button>
            <Button onClick={() => submit()} disabled={busy || !to || to < from} data-testid="printplus-confirm">
              <Printer className="h-4 w-4 mr-1" />{t("print_plus_confirm")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
